import { useState } from 'react';
import { Reveal, ProtectedImg, Eyebrow, SectionTitle, Glass, Disclaimer, SectionShell, PINK, PURPLE, GOLD } from './shared';

const GALLERY = [
  { name: 'campus-exterior.jpg', label: 'Campus arrival & creative courtyard', caption: 'An open, visible creative hub — performance, film and production on show.' },
  { name: 'campus-interior-studios.jpg', label: 'Multi-use creative interior', caption: 'Performance, training and collaboration spaces working side by side.' },
  { name: 'blackbox-theatre.jpg', label: 'Flexible black-box theatre', caption: 'A professional performance venue for student productions and public events.' },
  { name: 'creative-hub-atrium.jpg', label: 'Creative collaboration hub', caption: 'Shared atrium connecting studios, screen facilities and enterprise spaces.' },
  { name: 'aerial-production.jpg', label: 'Aerial training & production suites', caption: 'Potential aerial facilities alongside digital editing and screen production.' },
];

const FACILITIES = [
  'Flexible black-box theatre and performance venue', 'Acting and rehearsal studios', 'Dance and movement studios',
  'Green-screen and filmmaking facilities', 'Digital editing and sound production suites',
  'Technical theatre and production workshops', 'Creative collaboration areas',
  'Teaching rooms and student support spaces', 'Potential aerial and acrobatic training facilities',
];

const USES = [
  ['Daytime', 'College teaching and student productions.', PINK],
  ['Evenings', 'UPA classes, adult training, specialist workshops and creative projects.', PURPLE],
  ['Weekends', 'Saturday performing arts education, public performances and community activities.', GOLD],
  ['Additional availability', 'Rehearsal, screen production and industry collaboration.', '#3ED598'],
];

export const Campus = () => {
  const [sel, setSel] = useState(0);
  return (
    <SectionShell id="campus" data-testid="college-campus">
      <Reveal>
        <Eyebrow>The Campus</Eyebrow>
        <SectionTitle>A Creative Industries Hub — not just a college.</SectionTitle>
        <p className="text-white/65 max-w-3xl leading-relaxed">
          We envisage a multi-use creative hub serving students by day and the wider creative community year-round —
          spaces that teach the industry, from rehearsal to real production.
        </p>
      </Reveal>

      <Reveal>
        <div className="mt-12 rounded-2xl overflow-hidden border border-white/10 relative group">
          <div key={sel} className="college-fadeimg" style={{ animation: 'college-gallery-in 0.7s ease' }}>
            <ProtectedImg name={GALLERY[sel].name} alt={GALLERY[sel].label} className="w-full h-[320px] sm:h-[480px] object-cover transition-transform duration-[6000ms] group-hover:scale-[1.04]" />
          </div>
          <style>{`@keyframes college-gallery-in { from { opacity: 0.25; transform: scale(1.015); } to { opacity: 1; transform: scale(1); } } @media (prefers-reduced-motion: reduce) { [data-testid="college-campus"] .college-fadeimg { animation: none !important; } }`}</style>
          <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-black/90 to-transparent">
            <p className="text-white font-heading text-lg">{GALLERY[sel].label}</p>
            <p className="text-white/60 text-sm">{GALLERY[sel].caption}</p>
          </div>
        </div>
        <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
          {GALLERY.map((g, i) => (
            <button key={g.name} data-testid={`college-gallery-${i}`} onClick={() => setSel(i)}
              aria-label={`View: ${g.label}`} aria-pressed={sel === i}
              className={`shrink-0 w-28 h-16 rounded-lg overflow-hidden border-2 transition-colors ${sel === i ? 'border-[#FF007F]' : 'border-white/10 opacity-60 hover:opacity-100'}`}>
              <ProtectedImg name={g.name} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        <p className="text-white/40 text-xs mt-3 italic">
          Illustrative design concepts only. No premises, architectural design or development approvals have been secured.
        </p>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-8 mt-14">
        <Reveal>
          <Glass className="h-full">
            <h3 className="font-heading text-xl text-white mb-5">Proposed facilities</h3>
            <ul className="space-y-2.5">
              {FACILITIES.map(f => (
                <li key={f} className="flex items-start gap-3 text-white/70 text-sm">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: PINK }} aria-hidden="true" />{f}
                </li>
              ))}
            </ul>
          </Glass>
        </Reveal>
        <Reveal delay={120}>
          <div className="space-y-4">
            <h3 className="font-heading text-xl text-white mb-1">One campus. More than one audience.</h3>
            {USES.map(([title, body, color]) => (
              <Glass key={title} className="!p-5">
                <p className="font-semibold text-sm mb-1" style={{ color }}>{title}</p>
                <p className="text-white/65 text-sm">{body}</p>
              </Glass>
            ))}
          </div>
        </Reveal>
      </div>
    </SectionShell>
  );
};

const STAGES = [
  ['01', 'Open Submissions', 'Emerging writers submit original theatre and film scripts.'],
  ['02', 'Student Selection', 'A professional panel reviews submissions and students help select the projects they want to develop.'],
  ['03', 'Creative Development', 'Writers collaborate with student performers, filmmakers, technicians and professional mentors.'],
  ['04', 'Production', 'Selected work is rehearsed, performed, filmed and developed into finished creative projects.'],
  ['05', 'Public Showcase', 'Completed productions are presented to audiences, potentially including creative industry professionals.'],
];

export const Originals = () => (
  <section id="originals" data-college-section="originals" data-testid="college-originals"
    className="relative py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #050505, #10001f 50%, #050505)' }}>
    <div className="px-6 sm:px-10 lg:px-24 max-w-[1400px] mx-auto">
      <Reveal>
        <div className="inline-block px-4 py-1.5 rounded-full border border-[#FFD700]/40 text-[#FFD700] text-xs uppercase tracking-[0.3em] mb-6 font-semibold">
          A UPA Production Programme
        </div>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
          UPA <span style={{ background: 'linear-gradient(90deg,#FF007F,#FFD700)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>ORIGINALS</span>
        </h2>
        <p className="mt-3 text-lg sm:text-xl text-white/85 font-heading">New Voices. New Stories. New Opportunities.</p>
        <p className="mt-6 text-white/65 max-w-3xl leading-relaxed">
          A proposed development and production programme supporting emerging playwrights and screenwriters.
          Writers would have the opportunity to submit original theatre scripts and screenplays for consideration —
          developed and produced with UPA College students and professional mentors.
        </p>
      </Reveal>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4" role="list" aria-label="Five-stage production journey">
        {STAGES.map(([num, title, body], i) => (
          <Reveal key={num} delay={i * 130}>
            <div role="listitem" className="college-card relative h-full rounded-2xl border border-white/10 p-5 hover:border-[#FF007F]/50 transition-colors"
              style={{ background: 'linear-gradient(145deg, rgba(20,18,28,0.85), rgba(10,9,14,0.9))' }}>
              <p className="font-heading text-3xl mb-3" style={{ color: i % 2 ? PURPLE : PINK }}>{num}</p>
              <h3 className="text-white font-semibold text-sm mb-2">{title}</h3>
              <p className="text-white/55 text-xs leading-relaxed">{body}</p>
              {i < 4 && <span className="hidden lg:block absolute top-1/2 -right-3 text-white/30" aria-hidden="true">→</span>}
            </div>
          </Reveal>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-14">
        <Reveal>
          <Glass>
            <h3 className="font-heading text-xl text-white mb-4">Why it matters</h3>
            <p className="text-white/65 text-sm leading-relaxed mb-3">
              <span className="text-white font-semibold">For students:</span> original production experience, credited
              work on new writing, creative portfolios, showreels and genuine collaboration across every discipline.
            </p>
            <p className="text-white/65 text-sm leading-relaxed">
              <span className="text-white font-semibold">For independent writers:</span> a route to see new work
              developed, staged and filmed — with production support, mentorship and public presentation.
            </p>
          </Glass>
        </Reveal>
        <Reveal delay={120}>
          <Glass className="border-[#FFD700]/30">
            <p className="text-[#FFD700] text-xs uppercase tracking-[0.3em] mb-3 font-semibold">2027 Pilot Concept</p>
            <ul className="space-y-2 text-white/80 text-sm">
              <li>• One original stage production</li>
              <li>• One original short film</li>
              <li>• One public showcase event</li>
            </ul>
          </Glass>
          <Disclaimer>
            UPA Originals is a proposed programme and is not currently accepting submissions. Any future programme will
            require appropriate script-selection, copyright, licensing, safeguarding and production agreements.
          </Disclaimer>
        </Reveal>
      </div>
    </div>
  </section>
);

const RESORT_AREAS = [
  ['Live Entertainment', 'Performance, character work, live shows and guest-facing entertainment.', PINK],
  ['Technical Production', 'Lighting, sound, production support, stage management and show operations.', PURPLE],
  ['Film & Digital', 'Content creation, filmmaking, editing and multimedia storytelling.', GOLD],
  ['Creative Enterprise & Events', 'Marketing, events, production management and guest experience.', '#3ED598'],
];

const RESORT_TIMELINE = [
  ['2027', 'Aspirational first post-16 cohort, subject to approvals.'],
  ['2028', 'Industry projects and employer engagement.'],
  ['2029', 'Potential first graduates.'],
  ['2031', 'Planned opening of Universal United Kingdom Resort.'],
];

export const Industry = () => (
  <SectionShell id="industry" data-testid="college-industry">
    <Reveal>
      <Eyebrow color={PURPLE}>Regional Opportunity</Eyebrow>
      <SectionTitle>A once-in-a-generation employer on the doorstep.</SectionTitle>
      <p className="text-white/65 max-w-3xl leading-relaxed">
        Universal Destinations &amp; Experiences has publicly announced plans for Universal United Kingdom Resort in
        Bedfordshire, with an ambition to open in 2031 — one of the largest creative-sector employment opportunities
        the region has ever seen. UPA College could help prepare a local talent pipeline for relevant skills areas.
      </p>
    </Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
      {RESORT_AREAS.map(([t, b, c], i) => (
        <Reveal key={t} delay={i * 110}>
          <Glass className="h-full !p-5">
            <div className="w-8 h-1 rounded-full mb-4" style={{ background: c }} />
            <h3 className="text-white font-semibold text-sm mb-2">{t}</h3>
            <p className="text-white/55 text-xs leading-relaxed">{b}</p>
          </Glass>
        </Reveal>
      ))}
    </div>
    <Reveal>
      <div className="mt-14 relative">
        <div className="absolute top-5 left-[10%] right-[10%] h-px bg-gradient-to-r from-[#FF007F] via-[#7000FF] to-[#FFD700] hidden sm:block" aria-hidden="true" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {RESORT_TIMELINE.map(([year, body]) => (
              <div key={year} className="text-center">
                <div className="college-gold-pulse w-10 h-10 mx-auto rounded-full bg-[#0b0b10] border border-white/25 flex items-center justify-center relative z-10 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: GOLD }} aria-hidden="true" />
                </div>
              <p className="font-heading text-xl text-white">{year}</p>
              <p className="text-white/55 text-xs mt-1 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10 rounded-xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-5">
        <p className="text-white/75 text-sm leading-relaxed">
          <span className="font-semibold text-[#FFD700]">Important:</span> UPA has no existing formal partnership,
          affiliation or recruitment agreement with Universal Destinations &amp; Experiences. This section outlines a
          potential regional skills opportunity only.
        </p>
      </div>
    </Reveal>
  </SectionShell>
);
